import React, { useContext } from "react"
import {
  Container,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Stepper,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { UploadFileModalStep, FileTypes } from "../../get-reimbursed/steps/UploadFile"
import FileCategories from "../../../utils/FileCategories"
import { FormattedMessage, useIntl } from "react-intl"
import { useStateMachine } from "../../context/stateMachine/StateMachineV2"
import { UploadAdditionalDocumentsContext } from "../index"
import Settings from "../../..//utils/CarrotConfig"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import { getAmountsAndDates } from "#/components/carrot-card-cost-share/steps/shared/getAmountsAndDates"
import { DbgPaths } from "#/utils/Paths"
import { Link as RouterLink } from "react-router-dom"

function PreAuthAmountsAndDates(): JSX.Element {
  const { existingPreAuth, showPreAuthInfo } = useContext(UploadAdditionalDocumentsContext)
  const intl = useIntl()
  const amountsAndDates = getAmountsAndDates(existingPreAuth, intl)
  const theme = useTheme()
  const amountLabel = amountsAndDates.personalPaymentAmount
    ? intl.formatMessage({ defaultMessage: "Carrot Card Amount" })
    : intl.formatMessage({ defaultMessage: "Amount" })

  return showPreAuthInfo ? (
    <Stack direction="row" spacing={theme.tokens.spacing.lg}>
      <Stack spacing={theme.tokens.spacing.xxs} flex={1}>
        <Typography variant="overline" textTransform="uppercase">
          <FormattedMessage defaultMessage="Transaction Date" />
        </Typography>
        <Typography fontWeight="bold">{amountsAndDates.createdDate}</Typography>
      </Stack>

      <Stack spacing={theme.tokens.spacing.xxs} flex={1}>
        <Typography variant="overline" textTransform="uppercase">
          {amountLabel}
        </Typography>
        <Typography fontWeight="bold">{amountsAndDates.carrotCardAmount}</Typography>
      </Stack>

      {amountsAndDates.personalPaymentAmount && (
        <Stack spacing={theme.tokens.spacing.xxs} flex={1}>
          <Typography variant="overline" textTransform="uppercase">
            <FormattedMessage defaultMessage="Personal Payment Amount" />
          </Typography>
          <Typography fontWeight="bold">{amountsAndDates.personalPaymentAmount}</Typography>
        </Stack>
      )}
    </Stack>
  ) : null
}

const UploadAdditionalDocumentsBody = (): JSX.Element => {
  const theme = useTheme()
  const maxFileSize = Settings.MAX_FILE_SIZE / 1000000
  const { showPreAuthInfo, existingPreAuth } = useContext(UploadAdditionalDocumentsContext)
  const intl = useIntl()
  const amountsAndDates = getAmountsAndDates(existingPreAuth, intl)

  function getContentBasedOnExpenseType(): JSX.Element {
    if (showPreAuthInfo) {
      return existingPreAuth.memberOOPAmount ? (
        <FormattedMessage
          defaultMessage="Please upload your itemized statement and any other <link>required documents</link> for your {carrotCardAmount} Carrot Card transaction and {personalAmount} personal payment."
          values={{
            link: (linkContent) => (
              <Link
                target="_blank"
                component={RouterLink}
                to={DbgPaths.REQUIRED_DOCS}
                href={"/messages"}
                color="inherit"
              >
                {linkContent}
              </Link>
            ),
            carrotCardAmount: amountsAndDates.carrotCardAmount,
            personalAmount: amountsAndDates.personalPaymentAmount
          }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="Please upload your itemized statement and any other <link>required documents</link> for your {carrotCardAmount} Carrot Card transaction."
          values={{
            link: (linkContent) => (
              <Link
                target="_blank"
                component={RouterLink}
                to={DbgPaths.REQUIRED_DOCS}
                href={"/messages"}
                color="inherit"
              >
                {linkContent}
              </Link>
            ),
            carrotCardAmount: amountsAndDates.carrotCardAmount
          }}
        />
      )
    }
    return (
      <FormattedMessage
        defaultMessage="Add the files <link>requested by your Care Team</link>."
        values={{
          link: (linkContent) => (
            <Link target="_blank" href={"/messages"} color="inherit">
              {linkContent}
            </Link>
          )
        }}
      />
    )
  }
  return (
    <Stack spacing={theme.tokens.spacing.md}>
      <Typography>{getContentBasedOnExpenseType()}</Typography>

      <Typography>
        <FormattedMessage
          defaultMessage="We can accept jpg, jpeg, png, pdf, HEIC, doc, docx, txt, xlsx, and zip files up to {maxFileSize} MB."
          values={{ maxFileSize }}
        />
      </Typography>
      <PreAuthAmountsAndDates />
    </Stack>
  )
}

export const UploadAdditionalDocuments = (): JSX.Element => {
  const theme = useTheme()
  const { send } = useStateMachine(null)
  const { files, setFiles } = useContext(UploadAdditionalDocumentsContext)
  const textDirection = useTextDirection()

  const onContinueClick = (attachments: FileTypes): void => {
    setFiles(attachments)
    send("")
  }

  return (
    <Container>
      <Stepper
        dir={textDirection}
        variant="progress"
        steps={4}
        activeStep={1}
        position="static"
        sx={{ padding: "8px 0" }}
      />
      <Stack spacing={theme.tokens.spacing.lg}>
        <DialogContent>
          <UploadFileModalStep
            fileCategory={FileCategories.STATEMENT}
            header={
              <DialogTitle id="modal-title">
                <FormattedMessage defaultMessage="Upload more documents " />
              </DialogTitle>
            }
            body={<UploadAdditionalDocumentsBody />}
            onContinueClick={onContinueClick}
            files={files}
            useArrowButton={false}
          />
        </DialogContent>
      </Stack>
    </Container>
  )
}
